// packages/core/src/learning/school-modules/real-return.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'real-return' school. LearningModule, surface 'school'.
//   module id   = 'school-real-return'  (matches exhibit nativeModuleId)
//   homeSchool  = 'real-return'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_REAL_RETURN_MODULE: LearningModule = {
	id: 'school-real-return',
	title: 'Real vs Nominal Return',
	description:
		'The lens that measures money by purchasing power — nominal illusion, personal inflation, and the quiet drag of fees and taxes.',
	emoji: '💸',
	accentColor: '#9575cd',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'real-return',
	lessons: [
		{
			id: 'school-real-return-01',
			title: 'The number is not the wealth',
			description:
				'This lesson teaches why real return cares about what money can buy, not just how large the balance looks.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['real-return'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['real-return', 'purchasing-power', 'money-illusion'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['purchasing power', 'nominal illusion', 'real progress'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A bigger balance can feel like progress, but real return asks a harder question: what does that balance actually buy? If prices rise at the same time, the number may look better while your real life options barely improve.',
				},
				{
					type: 'paragraph',
					text: 'The real return lens separates nominal money from real purchasing power. Nominal is the number you see. Real is what that number can do in the world. This school is paying attention to the space between those two things.',
				},
				{
					type: 'bullet_list',
					items: [
						'Bigger dollars can hide weaker buying power',
						'Higher income can disappear into higher costs',
						'Real progress should change your options',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is celebrating the headline number without asking what happened to rent, food, insurance, taxes, and fees.',
				},
				{
					type: 'example',
					text: 'Imagine your income rises by $300 a month, but rent, groceries, gas, and insurance rise by the same amount. Your paycheck looks better, but your breathing room did not really change.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can make progress feel less exciting. If you subtract every cost, every tax, and every price increase, you may start feeling like nothing counts. That is not the point. The point is to tell the truth about what changed.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens connects to your Dashboard. Look at the money picture you have now, then ask whether your balance, income, and savings are creating more real options or just keeping pace with a more expensive life.',
				},
				{
					type: 'key_takeaway',
					text: 'Real wealth is not the number, it is what the number can do.',
				},
			],
			audioScript:
				'Real return starts with a simple but uncomfortable idea: the number is not the wealth. The number matters, of course. A bigger paycheck matters. A bigger savings balance matters. A growing portfolio matters. But those numbers are only part of the story. The real question is, what can the money actually do for your life?\n\nThat is where purchasing power comes in. Purchasing power means what your dollars can buy. If your account balance goes up, but everything you need also gets more expensive, your real progress may be smaller than it looks. You might have more dollars and still feel the same pressure. You might earn more and still feel like you are running in place. You might see a bigger number on paper but not have more freedom, more runway, or more choices.\n\nReal return is the lens that refuses to be fooled by surface numbers. It asks, after prices rise, after taxes hit, after fees take their cut, after rent goes up, after insurance goes up, after lifestyle costs creep up, what is left? Not in a negative way. In an honest way. Because the part that is left is the part that changes your options.\n\nThink about someone who gets a raise. Their paycheck increases by $300 a month. That sounds good, and it is better than no raise. But then rent goes up by $150. Groceries are $75 more. Insurance rises. Gas rises. Subscriptions stack up. Suddenly the raise does not feel like a raise. The person is not imagining things. Their nominal income improved, but their real breathing room barely changed.\n\nThis lens protects you from money illusion. Money illusion is when bigger numbers feel like progress even when buying power did not really improve. It happens with wages. It happens with investing. It happens with savings. It happens with lifestyle. You see more dollars, so your brain says, I am ahead. Real return says, maybe. But check what those dollars can buy.\n\nThe blind spot is that this lens can become discouraging if you take it too far. If every gain gets picked apart, you may lose motivation. That is not useful either. The point is not to make progress feel fake. The point is to make progress more accurate. A raise still matters. A growing balance still matters. Saving still matters. But the cleanest version of progress is when the numbers create more real options, not just bigger labels.\n\nInside Nisk, this belongs on your Dashboard. Look at your income, expenses, savings, and future path. Then ask: am I gaining actual room, or am I just keeping up with a more expensive life? That question is not meant to shame you. It is meant to sharpen you. Real wealth is not the number by itself. Real wealth is what the number lets you do, avoid, choose, and survive.',
			flashcards: [
				{
					id: 'school-real-return-01-fc-1',
					role: 'main_idea',
					front: 'What does real return measure?',
					back: 'It measures money by purchasing power, not just the visible dollar amount.',
				},
				{
					id: 'school-real-return-01-fc-2',
					role: 'watch_out',
					front: 'What is money illusion?',
					back: 'Thinking bigger numbers mean progress when buying power has not improved.',
				},
				{
					id: 'school-real-return-01-fc-3',
					role: 'use_it',
					front: 'How do you apply this lens?',
					back: 'Compare your numbers against real costs, not just account balances.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check real progress',
			},
			revision: 1,
		},
		{
			id: 'school-real-return-02',
			title: 'Inflation is personal before abstract',
			description:
				'This lesson teaches why real return treats rising prices as a force that changes daily choices and future freedom.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['real-return'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 2,
			tags: ['real-return', 'personal-inflation', 'cost-pressure'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'personal inflation',
				'cost pressure',
				'freedom erosion',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Inflation can sound like a headline, but real return sees it as personal. It shows up when rent rises, groceries cost more, insurance jumps, and the same life quietly needs more money to maintain.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes inflation is not only an economic statistic. It is a pressure on your options. When the cost of normal life rises, your savings, income, and future plans have to work harder just to stand still.',
				},
				{
					type: 'bullet_list',
					items: [
						'Your personal costs matter',
						'National averages can miss your life',
						'Rising prices can shrink future freedom',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating inflation like background noise until it shows up inside your monthly spending.',
				},
				{
					type: 'example',
					text: 'A national inflation number might look manageable, but your personal life may be hit harder if rent, car insurance, childcare, or food are the categories rising fastest for you.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that inflation thinking can turn into fear if you only focus on what is getting worse. The point is not panic. The point is building plans that notice rising costs before they surprise you.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens connects to Simulate. Test what happens if your future costs rise faster than expected. The goal is not prediction. The goal is seeing how sensitive your plan is to a more expensive life.',
				},
				{
					type: 'key_takeaway',
					text: 'Inflation matters when it changes what your money can still do.',
				},
			],
			audioScript:
				'Inflation is easy to ignore when it sounds like a national statistic. A number appears in the news. People argue about it. Economists explain it. Politicians spin it. Then most people go back to regular life. But real return does not treat inflation as some distant concept. This lens sees inflation as personal because it shows up in the places you actually live.\n\nIt shows up in rent. It shows up in groceries. It shows up in car insurance, phone bills, utilities, medical costs, subscriptions, travel, repairs, and the price of basic convenience. You do not experience inflation as one clean average. You experience it through your own spending mix. If the things you personally need are rising faster than the average, your life may feel more expensive even when the headline number seems manageable.\n\nThat is why real return thinking matters. It asks whether your money is growing faster than your real cost of living. If your income rises by 4 percent, but the things you actually buy rise by 6 percent, your paycheck may look better while your options shrink. If your savings balance is growing slowly, but your future rent target is growing faster, your goal may be moving away from you even while you are trying.\n\nThis lens protects you from treating inflation like background noise. Rising prices are not just annoying. They can change your timeline. They can reduce runway. They can make a move out date harder. They can make a car decision heavier. They can make emergency savings feel smaller. They can turn a plan that looked comfortable into a plan that needs more margin.\n\nHere is the important part: inflation is not the same for everyone. Someone living with family might feel it mostly through food, gas, and insurance. Someone renting alone might feel it through housing first. A parent might feel it through childcare. Someone with medical needs might feel it through health costs. A national average cannot perfectly describe your life. Your personal inflation is the one that hits your actual choices.\n\nThe blind spot is that inflation thinking can become fear based. You can spend so much time noticing rising costs that everything starts to feel hopeless. That is not the goal. Real return is not asking you to panic. It is asking you to plan with your eyes open. Inflation does not mean progress is impossible. It means progress needs to be measured honestly.\n\nInside Nisk, Simulate is useful here because you can test sensitivity. What if future costs are higher than expected? What if your surplus shrinks? What if your lifestyle costs rise faster than your income? The goal is not to predict the future perfectly. Nobody can do that. The goal is to understand whether your plan has enough room for a world where normal life keeps getting more expensive.\n\nInflation matters because it changes what your money can still do. Real return teaches you to look past the headline and ask the better question: are my dollars creating more options, or are they just fighting to keep the same life affordable?',
			flashcards: [
				{
					id: 'school-real-return-02-fc-1',
					role: 'main_idea',
					front: 'Why is inflation personal?',
					back: 'You feel inflation through the actual costs in your own life.',
				},
				{
					id: 'school-real-return-02-fc-2',
					role: 'watch_out',
					front: 'What does inflation quietly reduce?',
					back: 'It can reduce runway, savings power, and future options.',
				},
				{
					id: 'school-real-return-02-fc-3',
					role: 'use_it',
					front: 'How do you test inflation?',
					back: 'Simulate higher future costs and see how your plan reacts.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate rising costs',
			},
			revision: 1,
		},
		{
			id: 'school-real-return-03',
			title: 'Fees and taxes count too',
			description:
				'This lesson teaches why real return looks at what stays with you after taxes, fees, and other quiet forms of drag.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['real-return'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['real-return', 'fee-drag', 'after-cost-return'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['after cost return', 'fee drag', 'kept money matters'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A return can look strong before costs, then feel much weaker after everything takes a bite. Real return notices the quiet drag: fees, taxes, friction, bad timing, and costs that reduce what actually stays with you.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes the kept result matters more than the advertised result. The return you see before costs is not the same as the progress you keep after costs. That difference can shape your freedom path over time.',
				},
				{
					type: 'bullet_list',
					items: [
						'Fees reduce compounding',
						'Taxes affect what stays',
						'Small drags can matter when repeated',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging an investment, raise, or decision by the headline gain instead of the after cost result.',
				},
				{
					type: 'example',
					text: 'A portfolio might show growth, but if fees, taxes, and poor timing reduce the result, the real improvement may be smaller than the chart suggests.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that focusing too much on drag can make you obsess over tiny costs while ignoring bigger decisions. A small fee matters less than a weak savings habit, unstable income, or a lifestyle that absorbs every raise.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens is a reflection and testing lens. Look at your Dashboard, then ask what part of progress actually stays with you after costs. The goal is cleaner thinking, not perfection.',
				},
				{
					type: 'key_takeaway',
					text: 'The money that counts most is the money that stays useful after the drag.',
				},
			],
			audioScript:
				'Real return cares about what stays with you. That sounds obvious, but many people judge money decisions by the number before costs. They look at the raise before taxes. The investment return before fees. The sale price before repairs. The rental income before maintenance. The side income before expenses. The portfolio gain before taxes. The headline number feels exciting, but it is not always the number that changes your life.\n\nThis lens asks a cleaner question: after everything takes its bite, what is left? That does not mean every cost is bad. Taxes are part of the system. Some fees pay for real services. Some expenses are worth it. But if you ignore the drag, you may overestimate your progress. You may think a decision is doing more for your future than it actually is.\n\nFee drag is one example. A small ongoing fee can look harmless in one month or one year. But when it repeats for a long time, it can reduce the amount that compounds for you. Taxes are another example. A gain before taxes is not the same as a gain after taxes. Timing can matter too. Buying, selling, moving money, and changing plans can create friction. Even lifestyle can act like a tax on progress if every raise gets absorbed before it becomes freedom.\n\nThink about a person who earns extra income from a project. They say, I made $1,000. That sounds good. But then they subtract platform fees, supplies, gas, taxes, extra meals out because they were busy, and the time cost of doing the work. The real result may still be worth it. Or it may be much smaller than it first looked. Real return does not say the project was bad. It says measure the kept result.\n\nThe same idea applies to investing. A chart might show a gain. But if costs, taxes, timing mistakes, or constant tinkering reduce the benefit, the real return may be smaller than the emotional story. This lens protects you from getting impressed too easily by numbers that do not survive contact with reality.\n\nThe blind spot is obsession. You can focus so much on tiny costs that you miss the bigger levers. Avoiding a small fee matters less if your savings habit is weak. Tax efficiency matters, but it does not replace earning, saving, patience, and risk control. Real return thinking should sharpen your judgment, not trap you in tiny optimization.\n\nInside Nisk, this is a Dashboard and reflection lens. Look at income, expenses, savings, and progress. Then ask: what part of this actually stays useful after costs? What looks good before drag but weaker after drag? What big leak matters more than the small leak I keep obsessing over?\n\nThe goal is not perfection. The goal is honest measurement. The money that counts most is not the flashiest number. It is the money that survives the drag and still gives you options.',
			flashcards: [
				{
					id: 'school-real-return-03-fc-1',
					role: 'main_idea',
					front: 'What is after cost return?',
					back: 'It is the result that remains after fees, taxes, and other drag.',
				},
				{
					id: 'school-real-return-03-fc-2',
					role: 'watch_out',
					front: 'What is the headline gain trap?',
					back: 'Judging progress by the big number before costs.',
				},
				{
					id: 'school-real-return-03-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Ask what part of progress actually stays useful after costs.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find the quiet drag',
				prompt: 'Write one money area where the headline number may look better than the kept result.',
			},
			revision: 1,
		},
	],
};
